import { useRef, useState, useEffect, useMemo, useCallback } from "react"
import { throttle } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  leaveGame,
  selectChatMessages,
  selectGame,
  selectIsGameFinished,
  selectSpectatingAs,
  selectViewerId,
  selectWatchingThroughFog,
  selectLiveGame,
  selectIsLookingBack,
  selectRoundTrail,
  selectRoundMotion,
  clearRoundMotion,
  clearHistory,
  setSpectatingAs,
  setWatchingThroughFog,
  startWebSocket,
  selectResearchLevels,
} from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { selectEquippedCosmetics, selectMyAvatar } from "src/store/economy"
import { selectUserId } from "src/store/user"
import {
  UIWatcher,
  cancelMovement,
  stopPickingOrigin,
  clickCity,
  clearStrike,
  clearHover,
  startStrikeFromCity,
  hoverCity,
  selectSelectedNodes,
  selectStrikeOrigin,
  selectStrikeTarget,
  selectFocusedCity,
  selectCreatingMovement,
  selectSelectedCity,
  selectMovementTroops,
  selectStackFacingForGame,
  rememberFacings,
} from "src/store/ui"
import { activeWorld, mapSizeOf, setActiveWorld } from "src/utils/world"
import { getPlace } from "src/utils/maps"
import { readiness } from "src/utils/crowns"
import { buildBoard, cellOpensAView, standoffCells } from "src/utils/mapBoard"
import {
  atSpeed,
  boundariesOf,
  buildTimeline,
  clockNow,
  finalFacings,
  nextShowSpeed,
  soundCues,
  stateAt,
} from "src/utils/roundMotion"
import { loadShowSpeed, saveShowSpeed } from "src/utils/showSpeed"
import { playUiSound } from "src/utils/audio"
import { manhattan, strikeRange, visionRangeFor } from "src/utils/cellActions"
import { teamVisionKeys } from "src/utils/fog"
import { reachForStack } from "src/utils/movementEta"
import { isSurvival } from "src/utils/gameModes"
import constants from "src/constants"
import { addMessage } from "src/store/messages"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import useEscapeBacksOut from "src/hooks/useEscapeBacksOut"
import BaseLayout from "src/layouts/BaseLayout"
import Canvas, { asksForStillness } from "src/components/Canvas"
import MapActionBar from "src/components/MapActionBar"
import CellTooltip from "src/components/CellTooltip"
import ReactionBar from "src/components/ReactionBar"
import ReactionOverlay from "src/components/ReactionOverlay"
import RoundFlash from "src/components/RoundFlash"
import SpawnTour from "src/components/SpawnTour"
import MatchIntro, { introSeen } from "src/components/MatchIntro"
import RoutePlanner from "src/components/RoutePlanner"
import useFogPreview from "src/hooks/useFogPreview"
import { saveFogPreview } from "src/utils/fogPreview"
import Chat from "src/components/Chat"
import MiniMap from "src/components/MiniMap"
import MapRadar from "src/components/MapRadar"
import MapControls from "src/components/MapControls"
import PlayerStatuses from "src/components/PlayerStatuses"
import TikTak from "src/components/TikTak"
import RoundHandler from "src/components/RoundHandler"
import TopBarStats from "src/components/TopBarStats"
import ReplayControls from "src/components/ReplayControls"
import useReplay from "src/hooks/useReplay"
import TroopsTab from "src/components/TroopsTab"
import GameRail from "src/components/GameRail"
import MatrixRain from "src/components/MatrixRain"
import GameTabBar from "src/components/GameTabBar"
import BottomSheet from "src/components/BottomSheet"
import Segmented from "src/components/Segmented"
import SeatPicker from "src/components/SeatPicker"
import { CommsIcon, RadarIcon, ReportIcon, TroopsIcon } from "src/components/icons"
import RoundReport from "src/components/RoundReport"
import HistoryCornerButton, { HistoryBanner } from "src/components/HistoryCornerButton"
import SurrenderButton from "src/components/SurrenderButton"
import TradeButton from "src/components/TradeButton"

import { DESKTOP, PHONE, shellFor, useViewport } from "src/utils/viewport"

import "./Game.css"

import useMusic from "src/hooks/useMusic"

const NO_FACINGS = {}

/* `review`: the replay route mounts this view with `{ fromHistory: true, preloaded }` — the
   steps it already fetched. Without it the view is the live game, which flips into the same
   review mode by itself the moment the game finishes.

   `training`: basic training mounts this view (owner, 2026-09-20) with the furniture the lesson
   has no use for taken off — the minimap, REACT, the chat, TRADE and SURRENDER, none of which the
   lesson's local server answers — and the grid and coordinates on, with no eye to switch them
   off: the coach names cells by their coordinates, so a learner who could hide them would be
   asked to press a square they cannot find. Everything the lesson does teach stays the real
   control in its real place. */
const Game = ({ review = null, training = false }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const game = useSelector(selectGame)
  /* Which world this game is played on (requirement 35, M13): S, M or L, and L for anything from
     before the sizes. Made active here, during the render, so every reader below — the canvas,
     the minimap, the fog, the routes — draws and measures this game's map. A replay's game and
     basic training's come through the same door. */
  const mapSize = mapSizeOf(game.mapSize)
  setActiveWorld(mapSize)
  /* Two ids, and the difference between them is the whole of spectating (owner's ask, 2026-09-04).

     `accountId` is who is signed in, and it is the only thing that decides whether an order may be
     *sent*: no player row means no hands, whatever the view is showing.

     `userId` is whose board is being drawn. For a player they are the same id and nothing here
     changes. A spectator picks a seat, `selectViewerId` returns it, and every panel below — the
     roster, the resource chips, the city modal, the map's own sprites — answers for that seat,
     unfogged, which is what makes watching a game a game rather than a map of coloured squares. */
  const accountId = useSelector(selectUserId)
  const myAvatar = useSelector(selectMyAvatar)
  const userId = useSelector(selectViewerId)
  const spectatingAs = useSelector(selectSpectatingAs)
  const ownPlayer = game.players.find((p) => p.user.id === accountId)
  const player = game.players.find((p) => p.user.id === userId) || ownPlayer
  // No player row of their own means this client is a spectator: full map, no hands. Everything
  // that acts hides below; the map, roster, chat and reports stay — and now so do the panels,
  // read through whichever seat they have picked.
  const isSpectator = !ownPlayer

  /* Out of the game, but the game is not over (owner's report, 2026-09-04). Surrender is per
     player — a team-wide one would end the game for teammates who are still fighting — so in a
     2v2 the board carries on and the surrendering player kept every hand they no longer had a use
     for: SURRENDER offered a second time, a READY that gated nothing, and no way home, because the
     router pins you to a live game and this one is still live for everybody else.

     So an eliminated player is a watcher with a door. The hands go, the panels stay — their side's
     war is worth following — and `routes.js` stops pinning them. */
  const isEliminated = Boolean(ownPlayer?.eliminated)

  /* Review mode (owner's ask, 2026-09-07): one view for the game, the game just finished, and a
     replay opened from the history. They were three screens — the live view, a game-over modal
     over it whose only way to the replay was a route change, and a replay view with a bar, a seat
     picker and exits of its own. Now the game view is the replay: when the game finishes, or
     when the replay route mounts it with the steps already fetched, the round clock gives way to
     the stepper, the hands go, every seat can be watched through, and the board is whichever
     snapshot `useReplay` has loaded into the store. The standings stay a modal — opened once at
     the finish, and reopenable from the bar — so the numbers are never more than a click from
     the board they describe. */
  const isFinished = useSelector(selectIsGameFinished)
  const reviewing = Boolean(review) || isFinished
  /* The screen that opens a game (owner, 2026-10-08): the players, their classes, looks, leagues
     and names, on the opening board only, once per game per browser. Never in a replay or the
     lesson, and never for a game already past its first round. */
  const [introClosed, setIntroClosed] = useState(() => introSeen(game.id))
  const showIntro = !introClosed && !training && !reviewing && game.tick === 0
  useEffect(() => setIntroClosed(introSeen(game.id)), [game.id])
  const fromHistory = Boolean(review?.fromHistory)
  /* How fast a round's show plays (owner, 2026-10-26, requirement 36 M10): x1, x2 or x4, a
     per-browser choice beside the grid eye. A press mid-show changes the pace from where it is. */
  const [showSpeed, setShowSpeed] = useState(loadShowSpeed)
  const showSpeedRef = useRef(showSpeed)
  showSpeedRef.current = showSpeed
  const replay = useReplay({
    gameId: game.id,
    active: reviewing,
    preloaded: review?.preloaded ?? null,
    defaultSeat: ownPlayer?.user.id ?? game.players[0]?.user.id ?? null,
    openAt: fromHistory ? "first" : "last",
    showSpeed,
  })
  /* Looking back at a past round of this live game (requirement 35, H1–H8): the board on screen is
     the store's `history`, laid over the live game by `selectGame`; `liveGame` is the game the
     socket last sent, which is what the clock, the digest and the corner button read. No hands
     while it is on (H4) — the past is not where an order goes. */
  const liveGame = useSelector(selectLiveGame) || game
  const lookingBack = useSelector(selectIsLookingBack)
  const historyBoard = useSelector((state) => state.game?.history?.game ?? null)
  // The round that just turned, while its caption shows — the live board only (`roundTrail`, off
  // the `game_next_round` message).
  const roundTrail = useSelector(selectRoundTrail)
  // Not over a board that has a show: the show draws that round's roads itself, and the arrows
  // came back for a second between the show's end and the caption's (owner, 2026-10-26). The
  // board's own lines appear when the show ends.
  const trail =
    !reviewing && !lookingBack && roundTrail?.tick === game?.tick && !game?.metadata?.motion?.steps?.length
      ? roundTrail.movements
      : null
  // Which way the standing stacks face: as the live rounds' shows left them in this browser; a
  // past board rebuilds it from the shows up to its round (game 249, round 23: they faced east).
  const savedFacings = useSelector((state) => selectStackFacingForGame(state, game?.id))
  const stackFacings = reviewing
    ? replay.facings || NO_FACINGS
    : lookingBack
    ? historyBoard?.facings || NO_FACINGS
    : savedFacings
  /* The round playing itself (requirement 36, M1–M7, P1–P3). This view owns the clock: it builds
     the timeline when a board arrives on screen, hands `playback` to the canvas (which redraws every
     frame) and the hidden-live set to the board builder (rebuilt only at the moments it changes,
     `playbackMark`), schedules a live round's sounds, and clears the socket's log — by its round —
     when a live show is over, which is what lets `RoundFlash` put the caption and the trail away.
     No show under reduce-motion: the board lands at once, and a fight of the viewer's still sounds. */
  const roundMotion = useSelector(selectRoundMotion)
  const roundMotionRef = useRef(roundMotion)
  roundMotionRef.current = roundMotion
  const watchingThroughFog = useSelector(selectWatchingThroughFog)
  const [playback, setPlayback] = useState(null)
  const [playbackMark, setPlaybackMark] = useState(0)
  // The show running now: its sounds and the end it reports, for the cue timers below.
  const showRunRef = useRef(null)
  const changeShowSpeed = useCallback(() => {
    const next = nextShowSpeed(showSpeedRef.current)
    saveShowSpeed(next)
    setShowSpeed(next)
    setPlayback((current) => (current ? atSpeed(current, next) : current))
  }, [])
  /* Every board is shown played (owner, 2026-10-26, `requirements/36` P1–P3): the log of the round
     that produced the board on screen rides in its metadata — off the socket and the loaded game for
     the live board (`selectRawGame`), off the steps for a past round and the replay, cut to the eyes
     being watched through by `fog.js` — and it plays whenever the board shown changes: a round
     turning, a reload, a step back or forward, the live round again, another seat's eyes. `showKey`
     is that board; a re-render keeps it and never replays. The sounds and the facings are the live
     board's only. */
  const showMode = reviewing ? "replay" : lookingBack ? "back" : "live"
  const showEyes = reviewing
    ? (replay.fogOn ? `seat:${replay.seatId}` : "all")
    : isSpectator && watchingThroughFog
    ? `seat:${userId}`
    : "own"
  // A past round still on its way (the first ‹ after a load fetches the steps): the board on
  // screen is still the live one, and its show is not the past round's to play (owner, 2026-10-26:
  // "for a frame ... my units doing the current movement"). Nothing plays until the past board lands.
  const pastLoading = lookingBack && !historyBoard
  const shownMotion = !pastLoading && game?.metadata?.motion?.steps?.length ? game.metadata.motion : null
  const showKey = `${game?.id}:${game?.tick}:${showMode}:${showEyes}`
  const shownRef = useRef({ game, motion: shownMotion })
  shownRef.current = { game, motion: shownMotion }
  const playedKey = useRef(null)
  /* The show due on this board, built while the board is drawn rather than after it: the frame the
     new board first lands in already hides what the show will reveal — its march lines, its stacks
     still on the road — and draws the moving figures at their start. Built in the effect, the
     first frame showed the board's lines and stacks for a few milliseconds (owner, 2026-10-26:
     "it flickers a little bit the original destination line"). Kept per `showKey`. */
  const dueShow = useMemo(() => {
    if (!shownMotion || asksForStillness()) return null
    const teamOf = (id) => game?.metadata?.users?.[String(id)]?.team
    const timeline = buildTimeline(shownMotion, {
      columns: activeWorld().columns,
      teamOf,
      viewerId: userId,
      standoffs: standoffCells(game?.metadata, teamOf),
    })
    if (timeline.total <= 0) return null
    return { tick: game?.tick, timeline, startedAt: clockNow(), base: 0, speed: showSpeedRef.current }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKey, Boolean(shownMotion)])
  const dueShowRef = useRef(dueShow)
  dueShowRef.current = dueShow
  useEffect(() => {
    const { game: board, motion } = shownRef.current
    if (!motion || playedKey.current === showKey) return undefined
    playedKey.current = showKey
    const tick = board?.tick
    const live = showMode === "live"
    const teamOf = (id) => board?.metadata?.users?.[String(id)]?.team
    const due = dueShowRef.current
    const timeline =
      due?.timeline ??
      buildTimeline(motion, { columns: activeWorld().columns, teamOf, viewerId: userId, standoffs: standoffCells(board?.metadata, teamOf) })
    // Who holds or garrisons a cell, for the battle sound: the log names only the stacks.
    const partiesAt = (key) => {
      const cell = board?.metadata?.cells?.[key]
      if (!cell) return []
      const garrisons = Object.entries(cell.metadata_by_user || {})
        .filter(([, entry]) => Object.values(entry?.units || {}).some((unit) => (unit?.number ?? 0) > 0))
        .map(([id]) => Number(id))
      return [cell.owner, ...garrisons].filter((id) => id != null)
    }
    const sounds = live ? soundCues(timeline, { viewerId: accountId, teamOf, partiesAt }) : []
    // Where the stacks end up facing, kept for the board after the show (and without one).
    if (live && board?.id != null) {
      dispatch(rememberFacings({ gameId: board.id, facings: finalFacings(timeline) }))
    }
    const run = { sounds, finished: false }
    run.done = () => {
      run.finished = true
      if (live && roundMotionRef.current?.tick === tick) dispatch(clearRoundMotion({ tick }))
    }
    showRunRef.current = run
    if (asksForStillness() || timeline.total <= 0) {
      // The round-turn moment no longer carries the battle sound when the log has a clash (M7), so
      // a viewer who gets no show still hears their fight.
      if (sounds.some(({ sound }) => sound === "battle")) playUiSound("battle")
      run.done()
      return undefined
    }
    setPlayback({ tick, timeline, startedAt: due?.startedAt ?? clockNow(), base: 0, speed: showSpeedRef.current })
    setPlaybackMark(0)
    return () => {
      setPlayback(null)
      // A show stopped before its end was not seen: the next mount plays it. React mounts every
      // component twice in development, and the first mount's cleanup used to leave the board
      // marked as played, so a reload showed nothing (2026-10-26).
      if (!run.finished && playedKey.current === showKey) playedKey.current = null
    }
    // The board shown is what this watches (`showKey`): a payload mid-show (a bot's ready) must not
    // restart it, which is why the board and its log are read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKey, Boolean(shownMotion)])
  /* The show's cues, on its own clock (M10): every boundary, sound and the end are timed from where
     the show is now at its speed, and timed again when the speed changes (`atSpeed` makes a new
     `playback`). A cue already behind the show is not replayed. */
  useEffect(() => {
    const run = showRunRef.current
    if (!playback || !run) return undefined
    const at = (time) => Math.max(0, (time - (playback.base || 0)) / (playback.speed || 1))
    const ahead = (time) => time > (playback.base || 0)
    const timers = boundariesOf(playback.timeline)
      .filter(ahead)
      .map((time) => setTimeout(() => setPlaybackMark(time), at(time)))
    run.sounds.filter(({ at: time }) => ahead(time)).forEach(({ at: time, sound }) => timers.push(setTimeout(() => playUiSound(sound), at(time))))
    timers.push(
      setTimeout(() => {
        setPlayback(null)
        run.done()
      }, at(playback.timeline.total))
    )
    return () => timers.forEach(clearTimeout)
  }, [playback])
  // A round that turned while a past board or the replay is on screen is not played over it: its
  // caption is let go, and the live board plays it when the player comes back to it.
  useEffect(() => {
    if (roundMotion && (reviewing || lookingBack)) dispatch(clearRoundMotion({ tick: roundMotion.tick }))
  }, [dispatch, roundMotion, reviewing, lookingBack])
  // The show on screen: the one playing, or the one due on a board that has just landed (above).
  const showing = playback ?? (dueShow && playedKey.current !== showKey ? dueShow : null)
  // What the round said waits for the show (owner, 2026-10-26: "display the events in the chat + bot
  // reactions at the end of the animations"): the report's lines and a bot's bubble land with it.
  const roundShowing = Boolean(showing)
  const hiddenLive = useMemo(
    () => (showing ? stateAt(showing.timeline, playback ? playbackMark : 0).hiddenLive : null),
    [showing, playback, playbackMark]
  )
  const canAct = Boolean(ownPlayer) && !isEliminated && !reviewing && !lookingBack
  // Send aid and Surrender stay in place while looking back, disabled (owner, 2026-10-05): taken
  // away, the round selector under them jumped up the dock on every press of ‹.
  const showsAid = Boolean(ownPlayer) && !isEliminated && !reviewing
  // Looking, not playing: a live spectator, or anybody in review. What is watched through a seat.
  const watching = isSpectator || reviewing
  // Whose map clicks open the inspector rather than the action bar: a watcher, and a player
  // looking back at a past round (H4) — who is not a watcher otherwise and keeps their own seat.
  const inspecting = watching || lookingBack
  // Whose eyes: the review hook owns the seat while reviewing, the store otherwise.
  /* A live spectator has two ways of looking (owner, 2026-09-29): "Spectator", the whole board —
     the default — and "Watching as" a seat, which lays that seat's fog over the board
     (`selectGame`, `utils/fog`), so the watcher sees exactly what that player sees. The panels
     answer for a seat either way; in "Spectator" it is the last one picked. */
  const pickSeat = (id) => {
    if (reviewing) return replay.setSeatId(id)
    dispatch(setSpectatingAs(id))
    if (isSpectator) dispatch(setWatchingThroughFog(true))
    return undefined
  }
  const seeEverything =
    isSpectator && !reviewing
      ? { on: !watchingThroughFog, onPick: () => dispatch(setWatchingThroughFog(false)) }
      : null
  const goHome = () => {
    dispatch(leaveGame())
    ModalService.hideAll()
    navigate("/")
  }

  /* A spectator watches *through* somebody (owner's ask, 2026-09-04) — the same control and the
     same machinery as the replay, because a replay and a live spectator are the same problem a
     round apart. Without a seat every panel here has nobody to answer for: an empty roster, a city
     view with no garrison, resource chips counting nothing. So one is picked on arrival and the
     view becomes that player's, unfogged.

     It only ever runs for a spectator. A player's `spectatingAs` stays null and `selectViewerId`
     falls through to their own id, which is what keeps a borrowed seat from ever growing a hand. */
  useEffect(() => {
    if (!isSpectator || reviewing) return undefined
    if (spectatingAs == null && game.players.length > 0) {
      dispatch(setSpectatingAs(game.players[0].user.id))
    }
    return undefined
  }, [dispatch, isSpectator, reviewing, spectatingAs, game.players])

  // Leaving hands the seat back, or the next game renders through somebody else's eyes.
  useEffect(() => () => {
    dispatch(setSpectatingAs(null))
    dispatch(setWatchingThroughFog(false))
  }, [dispatch])
  // A finished game is a replay, fogged by its own eye; a spectator's live fog would lie on top.
  useEffect(() => {
    if (reviewing && watchingThroughFog) dispatch(setWatchingThroughFog(false))
  }, [dispatch, reviewing, watchingThroughFog])
  const playerMetadata = game.metadata.users[userId] || {}
  const highlightedNodes = useSelector(selectSelectedNodes)
  // What this player is wearing *now* (owner's R12): the game payload carries every player's
  // look, but it is only refreshed by the next broadcast — so a look equipped mid-game showed
  // in the shop and not on the map until something else happened. The wallet's answer is the
  // fresher copy for the viewer's own cells.
  const ownEquipped = useSelector(selectEquippedCosmetics)

  // Initialize the websocket for the game
  const [ready, setReady] = useState(false)
  const isInitialized = useRef(0)
  useEffect(() => {
    const prepare = async function () {
      if (!isInitialized.current) {
        isInitialized.current = true
        // A replay from the history has no live game to listen to: the snapshots are the feed.
        if (fromHistory) {
          setReady(true)
          return
        }
        await dispatch(startWebSocket({ gameId: game.id })).unwrap()
        console.log("Game: ", game)
        setReady(true)
      }
    }
    prepare()
  }, [dispatch, game, fromHistory])

  const viewport = useViewport()
  const shell = shellFor(viewport)
  const isCompact = viewport !== DESKTOP
  // Which pane the trimmed right dock is showing. On a desktop the round report and the chat are
  // stacked; at 280px they take turns, because two scrolling panes in one column is neither.
  const [dockPane, setDockPane] = useState("report")
  // Which sheet the phone has open, or null for the bare map. One at a time on purpose: two
  // half-height panels on a 390px screen is the desktop layout again, smaller.
  const [phoneSheet, setPhoneSheet] = useState(null)
  // Which pane the Forces sheet is showing. The two docks answer the same question on a phone —
  // who has what — so the troops panel and the player roster become segments of one sheet.
  const [forcesPane, setForcesPane] = useState("troops")
  // The chip row in the top bar scrolls sideways with no scrollbar; this is what tells the
  // stylesheet which edge has more behind it. See utils/scrollEdges.
  // The map's own bed. Leaving the view hands back to the menu's — see hooks/useMusic.
  useMusic("war")
  // Minimap click setup + update viewport
  const [clickOnMinimap, setClickOnMinimap] = useState(null)
  // The eye by the minimap: grid lines and coordinates off for a clean map. A per-browser choice.
  // Off until the player finds the eye (owner's R11, 2026-09-03): a new player faced a map of
  // numbers. The choice persists once made, either way. Basic training overrides it and offers
  // no eye — the lesson reads out coordinates — without touching the stored choice.
  const [gridChosen, setShowGrid] = useState(() => localStorage.getItem("hvz-show-grid") === "1")
  const showGrid = training || gridChosen
  const toggleGrid = useCallback(() => {
    setShowGrid((current) => {
      const next = !current
      try {
        localStorage.setItem("hvz-show-grid", next ? "1" : "0")
      } catch {
        // Storage unavailable: the choice lives for this session only.
      }
      return next
    })
  }, [])

  // The roster asked for a city: the canvas takes a position as map percentages, the same way the
  // minimap hands one over. Half a cell in so the floor on the other side lands on this cell.
  const focusedCity = useSelector(selectFocusedCity)
  useEffect(() => {
    if (!focusedCity) return
    setClickOnMinimap([
      ((focusedCity.col + 0.5) * 100) / activeWorld().columns,
      ((focusedCity.row + 0.5) * 100) / activeWorld().rows,
    ])
  }, [focusedCity])
  const [radarPosition, setRadarPosition] = useState(null)
  const throttledSetRadarPosition = useRef(
    throttle((position) => {
      setRadarPosition(position)
    }, 100) // Adjust the delay as needed
  )

  /* Where the camera is, kept in a ref rather than in state: the spawn tour has to be able to put
     the player back where it found them, and a camera in state would re-render this view on every
     pixel of every pan. The radar already pays that price, throttled; nothing else needs to. */
  const cameraRef = useRef(null)
  const readCamera = useCallback(() => cameraRef.current, [])

  // Use useCallback to memoize the function that calls the throttled function
  const onMoveUpdateRadar = useCallback((position, camera) => {
    cameraRef.current = camera || cameraRef.current
    throttledSetRadarPosition.current(position)
  }, [])

  /* The tour's hand on the camera (SV12): one `flyTo` at a time, handed to the canvas as a prop
     the same way `scrollTo` is. It is the only other thing that moves the map from outside, and
     it moves it in the canvas's own terms — see `Canvas`'s `flyTo`. */
  const [flyTo, setFlyTo] = useState(null)

  const gameConfig = useSelector(selectGameConfig)
  const selectedCity = useSelector(selectSelectedCity)
  const creatingMovement = useSelector(selectCreatingMovement)
  const strikeOrigin = useSelector(selectStrikeOrigin)
  // The silo's reach reads the viewer's missile-program level (S13, U6), the same way the server does.
  const researchLevels = useSelector(selectResearchLevels)
  const fogDisabled = Boolean(gameConfig?.disable_fog_of_war)
  const fogPreview = useFogPreview()

  // Selecting one of your (or an ally's) cells shows what it can see: everything beyond the owner's
  // detection radius is washed darker on the map, sea included. The radius is the server's —
  // base plus the thermal-suits line — so the ring matches what detection accepts.
  const focusRange = useMemo(() => {
    // Aiming a missile shows the silo's reach the same way: everything the shot cannot touch is
    // washed darker, so the player sees the range before committing the click — the fog-of-war
    // treatment, borrowed for ballistics.
    if (strikeOrigin && game?.metadata) {
      const silo = game.metadata.cells?.[`${strikeOrigin.col}-${strikeOrigin.row}`]?.metadata_by_user?.[
        String(userId)
      ]?.buildings?.[constants.buildings.human.MISSILE_SILO]
      return {
        col: Number(strikeOrigin.col),
        row: Number(strikeOrigin.row),
        radius: strikeRange(silo, gameConfig, researchLevels),
      }
    }
    if (!selectedCity || !game?.metadata) return null
    // Any cell, whoever holds it (owner, 2026-09-06: "when pressing any cell of the map we have
    // to see the range of vision, no matter if it's our city or what it is"). Whose radius is
    // `visionRangeFor`'s call: a teammate's city sees with the teammate's research, anything
    // else is read as "what would I see from here", with the viewer's own.
    return visionRangeFor({
      selected: selectedCity,
      cells: game.metadata.cells,
      users: game.metadata.users || {},
      userId,
      rules: gameConfig?.rules_config,
    })
  }, [selectedCity, strikeOrigin, game, userId, gameConfig, researchLevels])

  // What the team already looks over (owner's U3, 2026-09-07): the union of the rings around its
  // cities, camps and marches, each with its owner's research. The wash spares these squares, so
  // the ring on a pressed cell shows what that cell adds to the team's vision rather than
  // pretending nothing else can see. Not while aiming a missile — that wash is the silo's reach,
  // and what the team can see has no bearing on where a shot lands.
  const seenKeys = useMemo(() => {
    if (strikeOrigin || !selectedCity || !game?.metadata) return null
    return teamVisionKeys(game.metadata, userId, gameConfig?.rules_config)
  }, [selectedCity, strikeOrigin, game, userId, gameConfig])

  /* While a route is drawn, the cells the picked force reaches in ONE round from the route's end
     (owner, 2026-09-29): the map outlines that area, so "how far is one round" is read off the
     board before the click. A shade per round was tried first and read as a fog over the map.
     Recomputed only when the end of the route or the force changes. */
  const movementTroops = useSelector(selectMovementTroops)
  const routeEnd = creatingMovement && highlightedNodes.length ? highlightedNodes[0] : null
  const routeEndCol = routeEnd?.col
  const routeEndRow = routeEnd?.row
  const reach = useMemo(() => {
    if (routeEndCol == null || routeEndRow == null) return null
    const rounds = reachForStack({
      gameConfig,
      team: player?.team,
      troops: movementTroops,
      race: player?.race,
      researchLevels,
      from: { col: routeEndCol, row: routeEndRow },
      maxRounds: 1,
    })
    return rounds ? { rounds } : null
  }, [routeEndCol, routeEndRow, movementTroops, gameConfig, player?.team, player?.race, researchLevels])

  const canvas = useMemo(() => {
    const { elements, movements } = buildBoard({
      game,
      viewerId: userId,
      ownEquipped,
      highlightedNodes,
      selectedCity,
      creatingMovement,
      trail: trail || [],
      motion: hiddenLive,
      facings: stackFacings,
    })

    // On click locations. `hasCity` rides along so the reducer can answer a second click on the
    // already-selected cell by opening the city view directly — same rule as the CITY button
    // (a standing city, anyone's, or your own troops on unowned ground; empty land has none).
    const onClickLocation = (payload) => {
      dispatch(
        clickCity({
          ...payload,
          hasCity: cellOpensAView({ game, payload, viewerId: userId }),
          // No action bar for a watcher, so no first step for it to be.
          open: inspecting,
        })
      )
    }
    const onHoverLocation = (payload) => dispatch(hoverCity(payload))
    const onHoverEnd = () => dispatch(clearHover())
    // A press on the pick of a discharge of ours takes the order back (owner, 2026-10-23,
    // requirement 36 D5) — a hand, so only while the player may act.
    const onCancelDischarge = canAct
      ? ({ col, row }) => wsClient.send("game", { action: "cancel_discharge", column: col, row })
      : null

    // A spectator has no capital to open on; land on the first player's instead. Clicks stay
    // inert for them — selecting cells drives the action bar and city modals, which are hands.
    const home = player?.initialLocations?.[0] ??
      game.players[0]?.initialLocations?.[0] ?? {
        column: Math.round(activeWorld().columns / 4),
        row: Math.round(activeWorld().rows / 2),
      }
    // A replay from the history opens between the two sides (`cameraCentre`): its seats carry no
    // capitals, and the middle of everything owned is where the war is.
    const opening = fromHistory ? replay.initialPosition : [home.column, home.row]
    return (
      <Canvas
        key={mapSize}
        focusRange={focusRange}
        seenKeys={seenKeys}
        reach={reach}
        showGrid={showGrid}
        initialPosition={opening}
        scrollTo={clickOnMinimap}
        flyTo={flyTo}
        onMoveHooks={[onMoveUpdateRadar]}
        onClickHooks={player ? [onClickLocation] : []}
        onHoverHooks={[onHoverLocation]}
        onHoverEndHooks={[onHoverEnd]}
        elements={elements}
        // While a round plays, the canvas draws each march's line from its moving stack and leaves
        // the board's lines for after the show (owner, 2026-10-26); it needs them for that.
        movements={movements}
        motion={showing}
        onCancelDischarge={onCancelDischarge}
        fitToParent
      />
    )
  }, [clickOnMinimap, flyTo, onMoveUpdateRadar, highlightedNodes, focusRange, seenKeys, reach, showGrid, game, player, userId, inspecting, canAct, fromHistory, replay.initialPosition, dispatch, selectedCity, creatingMovement, ownEquipped, mapSize, trail, hiddenLive, showing, stackFacings])

  // Missile targeting: the silo sets an origin, the next map click sets the target, and the shot
  // goes out here so the modal does not have to stay mounted while the player picks.
  const strikeTarget = useSelector(selectStrikeTarget)
  // How far the silo at the origin reaches: level × radius-per-level, the server's own rule. Shown
  // in the banner, and enforced before the shot goes out so a long click is a warning, not a refusal.
  const strikeReach = strikeOrigin
    ? strikeRange(
        game.metadata?.cells?.[`${strikeOrigin.col}-${strikeOrigin.row}`]?.metadata_by_user?.[String(userId)]
          ?.buildings?.[constants.buildings.human.MISSILE_SILO],
        gameConfig,
        researchLevels
      )
    : 0
  useEffect(() => {
    if (!strikeTarget) return
    const origin = pendingStrikeOrigin.current
    if (origin) {
      const silo = game.metadata?.cells?.[`${origin.col}-${origin.row}`]?.metadata_by_user?.[String(userId)]
        ?.buildings?.[constants.buildings.human.MISSILE_SILO]
      const reach = strikeRange(silo, gameConfig, researchLevels)
      const distance = manhattan(origin, strikeTarget)
      if (distance > reach) {
        // Keep aiming: the origin is re-armed and the player told how far the silo reaches.
        dispatch(addMessage({ text: t("game.strike.outOfRange", { distance, reach }), type: "warning" }))
        dispatch(startStrikeFromCity(origin))
        return
      }
      // Never at your own side — the server refuses it too (FriendlyFire); catching it here keeps
      // the player aiming instead of ending the mode on a mistaken click.
      const targetOwner = game.metadata?.cells?.[`${strikeTarget.col}-${strikeTarget.row}`]?.owner
      const ownTeam = game.metadata?.users?.[String(userId)]?.team
      if (targetOwner != null && game.metadata?.users?.[String(targetOwner)]?.team === ownTeam) {
        dispatch(addMessage({ text: t("game.strike.friendlyFire"), type: "warning" }))
        dispatch(startStrikeFromCity(origin))
        return
      }
      wsClient.send("game", {
        action: "missile_strike",
        origin: { column: Number(origin.col), row: Number(origin.row) },
        target: { column: Number(strikeTarget.col), row: Number(strikeTarget.row) },
      })
    }
    pendingStrikeOrigin.current = null
    dispatch(clearStrike())
    // game/gameConfig are read for the rule; re-running on them would re-send a strike.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strikeTarget, dispatch])

  const pendingStrikeOrigin = useRef(null)
  useEffect(() => {
    if (strikeOrigin) pendingStrikeOrigin.current = strikeOrigin
  }, [strikeOrigin])

  // Escape backs out one layer at a time, and a press that closed a modal stops there.
  useEscapeBacksOut()

  /* Stepping into the past drops whatever order was half-given — a march being drawn, a silo
     aiming, "march here"'s origin list — since none of it may be finished there (H4). And the
     past is put away when this view goes (H8). */
  useEffect(() => {
    if (!lookingBack) return
    dispatch(cancelMovement())
    dispatch(clearStrike())
    dispatch(stopPickingOrigin())
  }, [dispatch, lookingBack])
  useEffect(() => () => dispatch(clearHistory()), [dispatch])

  /* The round's news is the right-hand report (`RoundReport`), and nothing more (owner, 2026-10-05:
     "remove the RONDA RESUELTA modal ... Make sure the events are in the right side bar"). The
     modal that opened over the map on every round that touched the player is gone. */

  // A finished game used to leave the player staring at an empty map with no explanation: their
  // cells are released and their units removed, so everything simply vanishes. The standings
  // open once, at the finish; a replay opened from the history already knows how it ended, and
  // the bar's RESULT button is there for the numbers.
  useEffect(() => {
    if (isFinished && !fromHistory) ModalService.show("GameOverModal")
  }, [isFinished, fromHistory])


  // "OPERATION <NAME>" uses the same coordinates → country lookup the movement cards and city
  // modals already use for a place name, read off the player's first initial location (their
  // capital). Water tiles or anything missing from worldData.json have no country entry, so this
  // falls back to the game id instead of leaving the title blank.
  const initialLocation = player?.initialLocations?.[0]
  const operationLocationName = initialLocation
    ? getPlace({ column: initialLocation.column, row: initialLocation.row }).name
    : null
  const operationLabel = operationLocationName ? operationLocationName.toUpperCase() : `${game.id}`
  // The server running without fog is a development setting and says nothing on screen
  // (owner, 2026-09-08): the top bar used to read "FOG OFF" beside the server id.
  /* "N PLAYERS" counts *people* in survival (SV16). The bots are the arena rather than the room:
     a lobby of two that meets sixteen enemies over sixty rounds would otherwise read "18 PLAYERS"
     in the bar, which is a count of nothing anybody asked about. Classic games are every seat, as
     they always were — a seated bot there is somebody's chosen opponent. */
  const peopleCount = isSurvival(game.mode)
    ? game.players.filter((seat) => !seat.user?.isBot).length
    : game.players.length
  const topBarSubtitleParts = [
    t("game.topBar.server", { id: game.id }),
    t("game.topBar.players", { count: peopleCount }),
  ]

  // The review's round stepper, over the foot of the map (`.replay-strip` in the map's bottom strip).
  const replayControls = reviewing ? (
    <ReplayControls
      playable={replay.playable}
      tick={replay.tick}
      total={replay.data?.tick ?? game.tick}
      shownIndex={replay.shownIndex}
      onTick={replay.setTick}
      playing={replay.playing}
      onTogglePlay={() => replay.setPlaying((value) => !value)}
    />
  ) : null

  const content = (
    // The map fills the centre dock. Canvas sizes its buffer to this box (`fitToParent`); sizing to
    // the window, as it once did, left a black strip beside the map and cut it off under the bar.
    <div className="game-map">
      {canvas}
      <UIWatcher />
      {/* The map's bottom strip: the orders for the selected cell, and REACT beside them. One row
          rather than two overlays that each measured the corner alone — the bar is sized by its
          content and REACT is anchored to the right, so on a small laptop the two met in the
          middle and REACT was drawn over the last button (owner, 2026-09-13). Laid out as a row,
          they cannot: when the pair no longer fits, REACT wraps onto the line above and the
          orders keep the strip, which is the bargain the phone's rail already makes. */}
      <div className="map-bottom-strip">
        {/* The commonest orders — march, spy, strike — answer on the map itself now; the city
            modal sits behind this bar's CITY button rather than opening on every click. */}
        {canAct && (creatingMovement ? <RoutePlanner /> : <MapActionBar />)}
        {/* A gesture anybody in the game can send, and everybody in the game sees. On the phone it
            is the rail's second slot; off it, the top of the map's button column (`MapControls`). */}
        {canAct && !training && viewport === PHONE && <ReactionBar />}
        {/* Looking back: the way home to the live round, in the seat the action bar leaves empty. */}
        <HistoryCornerButton />
        {/* The review's round stepper, over the foot of the map on every shell (owner,
            2026-10-09). It was the top bar's, where the phone's bar could not hold it beside RESULT
            and BACK TO BASE and it could not be pressed; moved down there, it read better on the
            desktop too. No order is given in a review, so it has the strip to itself. */}
        {reviewing && <div className="replay-strip">{replayControls}</div>}
      </div>
      {/* The minimap, on the board rather than under the dock (owner's ask, 2026-09-04). The phone
          keeps its own chip and sheet — there the panel covers the map, which is a different
          bargain, and it is the version this one was modelled on. */}
      {viewport !== PHONE && (
        <>
          {!training && <MapRadar setClickOnMinimap={setClickOnMinimap} radarPosition={radarPosition} />}
          <MapControls
            top={canAct && !training ? <ReactionBar /> : null}
            speed={{ value: showSpeed, onChange: changeShowSpeed }}
            gridVisible={showGrid}
            onToggleGrid={training ? undefined : toggleGrid}
            /* The fog-of-war preview, for a server running without fog (testing only). It was a
               banner-button pinned to the map's top-right — exactly where the radar now floats
               (owner's S19, 2026-09-05) — so it is a control among the controls instead; the
               settings pop-up keeps its switch and the explanation. The server does the
               redacting; this only flips the choice (see utils/fogPreview). */
            fogPreview={
              reviewing
                ? // In review the switch is always offered and flips the client's own fog over the
                  // snapshot (`utils/fog`), for the seat being watched — the steps arrive whole.
                  { on: replay.fogOn, onToggle: () => replay.setFogOn((value) => !value) }
                : fogDisabled
                  ? { on: fogPreview, onToggle: () => saveFogPreview(!fogPreview) }
                  : null
            }
          />
        </>
      )}
      {/* The chips that do not fit the phone's top bar, over the map's top-left corner (owner,
          2026-09-09). At 390px the bar held the food chip and hid the other four behind a fade —
          one of them the action budget, which is the number that decides what a player may do this
          round. Same component and the same chips, in the one place the phone has room for them;
          Food or meat comes first in that column (owner, 2026-09-29: "needs to be over the map, as all
          other icons"), so the bar no longer holds a chip of its own on this shell. */}
      {viewport === PHONE && player && (
        <div className="map-stats">
          <TopBarStats canEditFormation={canAct} />
        </div>
      )}
      {/* The coordinates and the show's speed on the phone's map too, under the radar chip and
          REACT: in the radar's sheet alone nobody found them (owner, 2026-10-26). */}
      {viewport === PHONE && !training && (
        <MapControls
          className="map-controls-phone"
          speed={{ value: showSpeed, onChange: changeShowSpeed }}
          gridVisible={showGrid}
          onToggleGrid={toggleGrid}
        />
      )}
      <CellTooltip />
      {/* Where the gestures land. The overlay is `pointer-events: none` so it never swallows a
          click meant for a cell underneath; the button that sends one is in the bottom strip. */}
      <ReactionOverlay holdBotLines={roundShowing} />
      {/* "The round turned." Every round announces itself here; what it did is the report's. */}
      <RoundFlash />
      {showIntro && (
        <MatchIntro game={game} accountId={accountId} myAvatar={myAvatar} onDone={() => setIntroClosed(true)} />
      )}
      <HistoryBanner />
      {/* A survival wave landing: the map goes to each newcomer in turn and comes back (SV12).
          Not in review — `reviewing` rather than `selectIsReviewing`, which a live spectator also
          sets, and a spectator is exactly who wants to be shown where the enemy landed. */}
      <SpawnTour active={!reviewing && !lookingBack} getCamera={readCamera} onFlyTo={setFlyTo} />
      {/* Watching, not playing — and the banner says which way: seeing everything ("Spectator"),
          where a viewer who cannot tell they hold the whole map would misjudge every fight, or
          through one player's fog ("Watching as"), where they see only what that player sees. */}
      {isSpectator && !reviewing && (
        <div id="spectator-banner">
          <span>
            {watchingThroughFog && player
              ? t("game.spectatorBannerAs", { username: player.user.username })
              : t("game.spectatorBanner")}
          </span>
          {/* The door (owner, 2026-09-06). The router never pinned a watcher, but the game rode
              along in their bootstrap for ever, so there was nothing to press: the server drops
              the seat with eyes, and the socket closing on the way out hands the clock over. */}
          <button
            type="button"
            onClick={() => {
              wsClient.send("game", { action: "stop_spectating" })
              dispatch(leaveGame())
              navigate("/")
            }}
          >
            {t("game.stopSpectating")}
          </button>
        </div>
      )}
      {/* Out of this game, which is still running for everybody else. The door is the point: the
          router no longer pins an eliminated player, and without a button saying so the only way
          out was to know that. */}
      {isEliminated && !reviewing && (
        <div id="eliminated-banner">
          <span>{t("game.eliminatedBanner")}</span>
          <button
            type="button"
            onClick={() => {
              dispatch(leaveGame())
              navigate("/")
            }}
          >
            {t("gameOver.backToBase")}
          </button>
        </div>
      )}
      {strikeOrigin && (
        <div id="strike-banner" className="p-small">
          {t("game.strike.pickTarget", { col: strikeOrigin.col, row: strikeOrigin.row, reach: strikeReach })}
          <button className="strike-cancel" onClick={() => dispatch(clearStrike())}>
            {t("game.strike.cancel")}
          </button>
        </div>
      )}
    </div>
  )
  const miniMap = (
    <MiniMap
      key={mapSize}
      setClickOnMinimap={setClickOnMinimap}
      radarPosition={radarPosition}
      gridVisible={showGrid}
      onToggleGrid={training ? undefined : toggleGrid}
    />
  )
  /* The dock's one panel (owner's ask, 2026-09-04). It was three tabs — Cities, Moves, Troops —
     and two of them were answering questions this one answers better: the roster of towns is here,
     each with what stands in it, and workers are units, so "can this city afford a building?" is
     read off the same row as "can it hold". A spectator has no forces of their own and so gets no
     panel; the rail drops the button with it. */
  /* On the narrow shells the top bar cannot hold the seat picker beside the clock and END TURN, so
     a watcher gets it above the roster instead — which is the panel it changes anyway. */
  const troopsPanel = player ? (
    watching && viewport !== DESKTOP ? (
      <div className="spectator-panel">
        <SeatPicker
          seats={game.players}
          value={spectatingAs}
          onChange={pickSeat}
          everything={seeEverything}
          className="panel-seats"
        />
        <TroopsTab />
      </div>
    ) : (
      <TroopsTab />
    )
  ) : null

  /* Narrow: the dock's contents are reference — you open the roster to pick a city, not
     continuously — so they stop holding 340px of permanent width and become a rail whose panels
     open over the map. */
  const railPanels = [
    ...(troopsPanel
      ? [
          {
            id: "troops",
            label: t("game.tabs.troops"),
            icon: <TroopsIcon />,
            node: troopsPanel,
            closeLabel: t("sheet.close"),
          },
        ]
      : []),
  ]

  const leftBar =
    viewport === PHONE ? null : isCompact ? (
    <GameRail panels={railPanels} ariaLabel={t("game.forces.title")} />
  ) : (
    /* The rain is the dock's ground and the roster stands on it (owner, 2026-09-10) — first
       child, and everything after it paints on top. Desktop only: the compact shell's left bar is
       a 56px rail and the phone has none, and neither is a place for a background nobody has room
       to see. A spectator has no roster, so their dock is the rain and nothing else. */
    <div id="left-bar-container">
      <MatrixRain />
      {troopsPanel}
    </div>
  )

  const chatMessages = useSelector(selectChatMessages)
  /* A replay from the history has no chat to show — messages are not persisted with the game —
     so the round report takes the whole dock rather than leaving a permanent empty box in the
     place a reader looks for what happened. A game that has just ended keeps its chat: the
     socket is still up, and the last word is often said here. */
  const hasChat = !fromHistory && !training
  /* The dock's three parts, at fixed shares of its height (owner, 2026-09-09): the roster and the
     two buttons take 30%, the round's events 40%, the chat the last 30%.

     Fixed, and that is the ask rather than a side effect. The dock was a column that grew from the
     top, so the roster took whatever it wanted — four seats, two of them bots with a stance chip —
     and the events took what was left, which in a 2v2 was a line and a half. The round report is
     the panel a player reads *between* rounds and it was the one being squeezed. At a share it is
     the same size in every game, whether or not there is anything in it — an empty pane is a
     smaller cost than a pane that changes size as the war goes on.

     Only where all three are on screen at once. The compact shell tabs the report and the chat
     into one pane, and a replay has no chat at all; both are different compositions rather than
     narrower versions of this one. */
  const thirds = hasChat && !isCompact

  const rightBar = (
    <div id="right-bar-container" className={thirds ? "right-bar-thirds" : undefined}>
      <div className="right-bar-roster">
        <PlayerStatuses game={game} userId={userId} replay={reviewing} />
        {showsAid && !training && (
          <div className="aid-row">
            <TradeButton disabled={lookingBack} />
            <SurrenderButton disabled={lookingBack} />
          </div>
        )}
      </div>
      {!hasChat ? (
        <div className="replay-events">
          <RoundReport held={roundShowing} />
        </div>
      ) : isCompact ? (
        <div id="dock-panes">
          <Segmented
            ariaLabel={t("game.roundReport.title")}
            value={dockPane}
            onChange={setDockPane}
            options={[
              { id: "report", label: t("game.roundReport.title") },
              { id: "chat", label: t("chat.title") },
            ]}
          />
          {/* Both stay mounted: the round report holds which round it is looking at and the chat
              holds its scroll, and unmounting either makes a glance at the other cost that. */}
          <div className={`dock-pane ${dockPane === "report" ? "" : "dock-pane-hidden"}`}>
            <RoundReport held={roundShowing} />
          </div>
          <div className={`dock-pane ${dockPane === "chat" ? "" : "dock-pane-hidden"}`}>
            <Chat chatKey="game" messages={chatMessages} people={game?.players} linkCoords />
          </div>
        </div>
      ) : (
        <>
          <div className="right-bar-events">
            <RoundReport held={roundShowing} />
          </div>
          <div id="chat-flex-wrapper">
            <Chat chatKey="game" messages={chatMessages} people={game?.players} linkCoords />
          </div>
        </>
      )}
    </div>
  )

  /* --- The phone's chrome ------------------------------------------------------------------
     No docks: the map is the floor, the panels are sheets over it, and the tab bar is how you
     move between them. The desktop's two docks collapse into three sheets, and they do not map
     one-to-one — the troops panel (left dock) and the player roster (right dock) both answer
     "who has what", so they become segments of one Forces sheet, which is a smaller information
     architecture than the desktop has rather than a squeezed copy of it. */
  /* The same number the desktop roster shows, from the same rule (`utils/crowns.readiness`):
     the seats that can still ready up — no eliminated players, and in survival no waves. Counted
     here as "every seat", a solo survival game read "3/4" on this tab while the roster said
     "0 / 1". */
  const { ready: readyCount, total: readyTotal } = readiness(game)

  const phoneSheets = {
    forces: {
      title: t("game.forces.title"),
      subtitle: t("game.forces.readyCount", { ready: readyCount, total: readyTotal }),
      body: (
        <div className="phone-sheet-stack">
          <Segmented
            ariaLabel={t("game.forces.title")}
            value={forcesPane}
            onChange={setForcesPane}
            options={[
              ...(player ? [{ id: "troops", label: t("game.tabs.troops") }] : []),
              { id: "players", label: t("game.tabs.players") },
            ]}
          />
          {forcesPane === "players" || !player ? (
            <PlayerStatuses game={game} userId={userId} hideHeader replay={reviewing} />
          ) : (
            <TroopsTab />
          )}
        </div>
      ),
    },
    report: { title: t("game.roundReport.title"), body: <RoundReport held={roundShowing} /> },
    comms: {
      title: t("chat.title"),
      body: (
        <div className="phone-sheet-stack">
          {showsAid && (
            <div className="aid-row">
              <TradeButton disabled={lookingBack} />
              <SurrenderButton disabled={lookingBack} />
            </div>
          )}
          <Chat chatKey="game" messages={chatMessages} people={game?.players} linkCoords />
        </div>
      ),
    },
    radar: { title: t("game.miniMap.alt"), body: miniMap },
  }
  if (!hasChat) delete phoneSheets.comms
  const openSheet = phoneSheet ? phoneSheets[phoneSheet] : null

  const phoneChrome = viewport !== PHONE ? null : (
    <>
      {/* The minimap stays on the map as a chip rather than taking a tab: it is a navigation aid
          for a map you can already drag. Zoom has no buttons here or anywhere (owner, 2026-09-20):
          the pinch is the phone's zoom as the wheel is the desktop's, and the pair that used to
          sit under this chip was a second way to do what the fingers already do. */}
      {!training && (
        <button
          type="button"
          className="phone-radar-chip"
          onClick={() => setPhoneSheet((current) => (current === "radar" ? null : "radar"))}
          aria-label={t("game.miniMap.alt")}
        >
          <RadarIcon size={22} />
        </button>
      )}

      {openSheet && (
        <BottomSheet
          open
          onClose={() => setPhoneSheet(null)}
          title={openSheet.title}
          subtitle={openSheet.subtitle}
        >
          {openSheet.body}
        </BottomSheet>
      )}

      <GameTabBar
        ariaLabel={t("game.topBar.operation", { name: operationLabel })}
        value={phoneSheet}
        onChange={setPhoneSheet}
        /* `game.tabBar.*` rather than each sheet's own title: a tab label and a panel heading are
           different lengths of thing, and English only got away with sharing them because "Comms"
           was already an abbreviation. In Spanish the same three keys are "Fuerzas", "Última
           ronda" and "Comunicaciones" — the second wrapped to two lines and knocked its icon off
           the row's baseline, the third ran under the auto-advance button. One word each. */
        tabs={[
          {
            id: "forces",
            label: t("game.tabBar.forces"),
            icon: <TroopsIcon />,
            badge: `${readyCount}/${readyTotal}`,
            badgeTone: "amber",
          },
          {
            id: "report",
            label: t("game.tabBar.report"),
            icon: <ReportIcon />,
            // Something happened last round and this player has not opened it yet.
            dot: game.tick > 0 && phoneSheet !== "report",
          },
          ...(hasChat ? [{ id: "comms", label: t("game.tabBar.comms"), icon: <CommsIcon /> }] : []),
        ]}
      >
        {/* The account's id, never the borrowed seat's: the handler decides "am I the host?"
            by it, and a spectator-host watching through a bot was handed the bot's id — so a
            bot-vs-bot game never turned a round (owner, 2026-09-06). Rendered for a spectator
            too, for the effects: it draws no button without a seat of its own. And for an
            eliminated player: a host knocked out with nobody but bots left standing keeps the
            clock (`games/host.hand_over_host_if_out` never picks a bot), and unmounting this
            stopped a 3v3 dead the round they fell. */}
        {!reviewing && <RoundHandler
            ready={ready}
            game={liveGame}
            userId={accountId}
            lookingBack={lookingBack}
            pauseRules={gameConfig?.rules_config}
          />}
      </GameTabBar>
    </>
  )

  // The fixed top command bar: team identity, the round clock (TikTak, restyled inline), the
  // resource stat chips that used to float over the map as #game-resources, the END TURN control
  // (RoundHandler, which now renders the button as well as running its existing auto-advance
  // effects), and a settings gear that replaces BaseLayout's floating user chip on this view.
  const topBar = (
    <div id="top-bar" className={`${viewport === PHONE ? "topbar-phone" : ""}${reviewing ? " topbar-review" : ""}`.trim()}>
      <div className="topbar-identity">
        {isSpectator ? (
          <div className="team-chip team-chip-spectator">{t("game.topBar.spectator")}</div>
        ) : (
          <div className={`team-chip team-chip-${playerMetadata.team}`}>{playerMetadata.team === "human" ? t("game.topBar.teamHuman") : t("game.topBar.teamZombie")}</div>
        )}
        {/* Identity, not state: which operation this is and which server it is on does not change
            during a round, so it gives its width to the clock and the resources wherever the bar
            is tight. That is not only the phone — at 768 the bar also carries STOP, END TURN and
            settings, and the block was left 99px, in which "Operation Turkey / SRV-24 · 2 players"
            renders as "OPERATION…" over "SRV-24 · 2 PL…". Two ellipses are not an identity. */}
        {viewport === DESKTOP && (
          <div className="topbar-title-block">
            <p className="topbar-title">{t("game.topBar.operation", { name: operationLabel })}</p>
            <p className="topbar-subtitle">{topBarSubtitleParts.join(" · ")}</p>
          </div>
        )}
      </div>

      <div className="topbar-round">
        {/* In review the clock goes: it counts down a round that will never come. Its stepper is
            over the foot of the map (`.replay-strip`), not here, since 2026-10-09. */}
        {!reviewing && <TikTak game={liveGame} />}
      </div>

      <div className="topbar-right">
        {/* A spectator picks whose game this is; the chips below then count that seat's food,
            research and actions, which is the whole of "watching through somebody". */}
        {watching && viewport === DESKTOP && (
          <SeatPicker
            seats={game.players}
            value={spectatingAs}
            onChange={pickSeat}
            everything={seeEverything}
            className="topbar-seats"
          />
        )}
        {/* The seat's readouts, live or reviewed. */}
        {/* On the phone every chip, food or meat first, is drawn over the map — see `.map-stats`
            in the content above. */}
        {player && viewport !== PHONE && <TopBarStats canEditFormation={canAct} />}

        {viewport !== PHONE && !reviewing && (
          <RoundHandler
            ready={ready}
            game={liveGame}
            userId={accountId}
            lookingBack={lookingBack}
            pauseRules={gameConfig?.rules_config}
          />
        )}
        {/* A finished game's two doors: the standings, and the way out. */}
        {isFinished && (
          <button type="button" className="topbar-result" onClick={() => ModalService.show("GameOverModal")}>
            {t("game.topBar.result")}
          </button>
        )}
        {/* The way home. A review had it; a live game has it since 2026-09-22, because the router
            no longer pins a player to a live game and the home page lists the wars they are in.
            Leaving closes this game's socket, which is what hands its clock over (`games/host`). */}
        <button
          type="button"
          className="topbar-back"
          onClick={goHome}
          title={reviewing ? t("replay.back") : t("game.topBar.home")}
        >
          ← {reviewing ? t("replay.back") : t("game.topBar.home")}
        </button>

        <button
          type="button"
          className="topbar-settings-button"
          onClick={() => ModalService.show("SettingsModal")}
          aria-label={t("game.topBar.settings")}
        >
          ⚙
        </button>
      </div>
    </div>
  )

  return (
    <>
      <BaseLayout content={content} leftBar={leftBar} rightBar={rightBar} topBar={topBar} layout={shell} />
      {/* Fixed chrome, so it does not need to live inside the map's box — and putting it outside
          keeps the sheets from being clipped by it. */}
      {phoneChrome}
    </>
  )
}

export default Game
